import { PlayerRef } from "@remotion/player";
import {
  Pause,
  Play,
  Redo,
  SkipBack,
  SkipForward,
  Undo,
  Minus,
  Plus,
} from "lucide-react";
import { useCurrentPlayerFrame } from "../hooks/useCurrentPlayerFrame";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";

// Format seconds as MM:SS
const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes.toString().padStart(2, "0")}:${secs
    .toString()
    .padStart(2, "0")}`;
};

interface TransportControlsProps {
  isPlaying: boolean;
  historyIndex: number;
  historyLength: number;
  zoom: number;
  playerRef: React.RefObject<PlayerRef | null>;
  durationInFrames: number;
  fps: number;
  onPlayPause: () => void;
  onSkipToStart: () => void;
  onSkipToEnd: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onZoomChange: (value: number) => void;
}

export function TransportControls({
  isPlaying,
  historyIndex,
  historyLength,
  zoom,
  playerRef,
  durationInFrames,
  fps,
  onPlayPause,
  onSkipToStart,
  onSkipToEnd,
  onUndo,
  onRedo,
  onZoomChange,
}: TransportControlsProps) {
  const currentFrame = useCurrentPlayerFrame(playerRef);

  const currentTimeSeconds = currentFrame / fps;
  const totalTimeSeconds = durationInFrames / fps;

  const currentTimeFormatted = formatTime(currentTimeSeconds);
  const totalTimeFormatted = formatTime(totalTimeSeconds);

  return (
    <div className="w-full flex items-center justify-between py-2">
      {/* TIME DISPLAY */}
      <div className="flex items-center gap-2 w-[200px]">
        <span className="text-sm font-bold text-slate-900 tabular-nums">{currentTimeFormatted}</span>
        <span className="text-xs font-bold text-slate-300">/</span>
        <span className="text-sm font-bold text-slate-400 tabular-nums">{totalTimeFormatted}</span>
      </div>

      {/* TRANSPORT BUTTONS */}
      <div className="flex items-center gap-1.5 bg-white border border-slate-200 p-0.5 rounded-xl shadow-sm">
        <Button
          variant="ghost"
          size="icon"
          onClick={onSkipToStart}
          className="w-8 h-8 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-50 transition-all"
        >
          <SkipBack size={16} />
        </Button>
        <Button
          onClick={onPlayPause}
          className={`w-10 h-8 rounded-lg transition-all shadow-md ${isPlaying
            ? "bg-slate-100 text-slate-900 hover:bg-slate-200"
            : "bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-100"
            }`}
        >
          {isPlaying ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" className="ml-0.5" />}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={onSkipToEnd}
          className="w-8 h-8 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-50 transition-all"
        >
          <SkipForward size={16} />
        </Button>

        <div className="w-px h-6 bg-slate-100 mx-1" />

        <Button
          variant="ghost"
          size="icon"
          disabled={historyIndex === 0}
          onClick={onUndo}
          className="w-8 h-8 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-50 transition-all disabled:opacity-30"
        >
          <Undo size={16} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          disabled={historyIndex === historyLength - 1}
          onClick={onRedo}
          className="w-8 h-8 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-50 transition-all disabled:opacity-30"
        >
          <Redo size={16} />
        </Button>
      </div>

      {/* ZOOM CONTROL */}
      <div className="flex items-center gap-4 w-[200px] bg-white border border-slate-200 p-0.5 px-3 rounded-xl shadow-sm">
        <button
          onClick={() => onZoomChange(Math.max(1, zoom - 1))}
          className="text-slate-400 hover:text-slate-900 transition-colors"
        >
          <Minus size={14} strokeWidth={3} />
        </button>

        <Slider
          className="flex-grow"
          value={[zoom]}
          min={1}
          max={Math.max(10, Math.ceil(zoom * 1.5))}
          step={1}
          onValueChange={(value) => onZoomChange(value[0])}
        />

        <button
          onClick={() => onZoomChange(zoom + 1)}
          className="text-slate-400 hover:text-slate-900 transition-colors"
        >
          <Plus size={14} strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}
